"use client";

import { JSX, useEffect, useMemo, useRef } from "react";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import type { ProcessStatus } from "@/components/charts/process-status";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import type { LegendSelectSource } from "@/components/charts/use-legend-click-sync";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  buildResolvedLegendItems,
  categoryKeyOf,
  hasCategoryGrouping,
  legendIndexOfCategory,
  resolveCategories,
  resolveCategoryColor,
} from "./shared/category-resolve";
import type { ModuleCategory } from "@/types/modules";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { importsTokenScope } from "./imports-token-scope";

export type ForkJoinNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  status?: ProcessStatus;
  categoryId?: string;
  type?: "junction";
};

export type ForkJoinEdge = [string, string];

export type ForkJoinData = {
  nodes: ForkJoinNode[];
  edges: ForkJoinEdge[];
  categories?: ModuleCategory[];
};

function legacyForkJoinCategories(t: Translate): ModuleCategory[] {
  return [
    { id: "done", label: t("Done"), color: "var(--status-1)" },
    { id: "active", label: t("Running"), color: "var(--emerald)" },
    { id: "pending", label: t("Waiting"), color: "var(--amber)" },
  ];
}

function legacyForkJoinCategoriesGrayscale(t: Translate): ModuleCategory[] {
  return [
    { id: "done", label: t("Done"), color: "var(--status-1)" },
    { id: "active", label: t("Running"), color: "var(--status-2)" },
    { id: "pending", label: t("Waiting"), color: "var(--status-neutral)" },
  ];
}

const DEFAULT_DATA: ForkJoinData = {
  nodes: [
    { id: "start", label: "Trigger", x: 5, y: 1, status: "done" },
    { id: "init", label: "Initialize", x: 20, y: 1, status: "done" },
    { id: "fork", label: "Fork", x: 34, y: 1, status: "done", type: "junction" },
    { id: "a1", label: "Fetch data", x: 50, y: 0.2, status: "done" },
    { id: "a2", label: "Transform", x: 68, y: 0.2, status: "active" },
    { id: "b1", label: "Validate", x: 50, y: 1.8, status: "done" },
    { id: "b2", label: "Enrich", x: 68, y: 1.8, status: "active" },
    { id: "join", label: "Merge", x: 82, y: 1, status: "pending", type: "junction" },
    { id: "final", label: "Deliver", x: 95, y: 1, status: "pending" },
  ],
  edges: [
    ["start", "init"],
    ["init", "fork"],
    ["fork", "a1"],
    ["fork", "b1"],
    ["a1", "a2"],
    ["b1", "b2"],
    ["a2", "join"],
    ["b2", "join"],
    ["join", "final"],
  ],
};

export default function ModuleForkJoin({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: ForkJoinData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const nodes = data.nodes ?? [];
  const edges = data.edges ?? [];
  const freeCategories = data.categories;
  const grouped = hasCategoryGrouping(nodes, freeCategories);
  const resolved = resolveCategories(
    freeCategories,
    grayscale ? legacyForkJoinCategoriesGrayscale(t) : legacyForkJoinCategories(t),
  );
  const free = resolved.free;
  const selection = useSelectionGroup();
  const node = useHoverSelect<string>({ group: selection });
  const effective = node.effective;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();
  const maskId = useSvgId("fj-mask");

  const W = 760;
  const H = 240;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [nodes, edges]);
  const padX = 60;
  const yCenter = H / 2;
  const rowOffset = 60;
  const chartW = W - padX * 2;
  const xFor = (x: number) => padX + (x / 100) * chartW;
  const yFor = (y: number) => yCenter + (y - 1) * rowOffset;

  const nodeById = useMemo(
    () =>
      Object.fromEntries(nodes.map((n) => [n.id, n])) as Record<string, ForkJoinNode>,
    [nodes],
  );

  const legendItems = buildResolvedLegendItems(nodes, resolved);
  const nodeKey = (n: ForkJoinNode) => categoryKeyOf(n, free);
  const legFilterStatus =
    legend.filter !== null ? legendItems[legend.filter]?.id ?? null : null;

  const selectedNode = node.selected ? nodeById[node.selected] : null;
  const legendSelectedIndex =
    legend.selected ??
    legendIndexOfCategory(
      legendItems,
      selectedNode ? nodeKey(selectedNode) : null,
    );

  // One selection for both surfaces: a legend row stands for the first node of
  // its category, so the highlight and the tooltip answer to either gesture
  // while only the legend's own pick narrows the graph.
  const onLegendSelect = (index: number, source?: LegendSelectSource) => {
    const releasing = legend.selected === index;
    legend.select(index, source);
    const key = legendItems[index]?.id;
    const firstNode = key
      ? nodes.find((n) => nodeKey(n) === key)?.id ?? null
      : null;
    node.setSelected(releasing ? null : firstNode);
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activeNode = effective ? nodeById[effective] : null;
  const activeKey = activeNode ? nodeKey(activeNode) : undefined;
  const activeStatusLabel = activeKey
    ? legendItems.find((li) => li.id === activeKey)?.label ?? null
    : null;
  const activeStatusColor = activeKey
    ? legendItems.find((li) => li.id === activeKey)?.color ?? "var(--muted-foreground)"
    : "var(--muted-foreground)";
  // Both surfaces feed one scroll target: a legend row centres the first node
  // of its category, a node picked in the graph is only pulled in when it sits
  // outside the viewport. Keying the effect on the resolved id instead of the
  // rebuilt arrays keeps it from firing on unrelated renders, which is what
  // makes re-picking the same category scroll again.
  const scrollCategory =
    legend.selected !== null ? legendItems[legend.selected]?.id ?? null : null;
  const scrollNodeId =
    scrollCategory !== null
      ? nodes.find((n) => nodeKey(n) === scrollCategory)?.id ?? null
      : node.selected;
  const scrollAlign = scrollCategory !== null ? "center" : "nearest";
  useEffect(() => {
    if (scrollNodeId === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-part-index="${scrollNodeId}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollNodeId, scrollAlign]);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <g mask={`url(#${maskId})`}>
            {[H * 0.2, H * 0.5, H * 0.8].map((y) => (
              <line
                key={y}
                x1={0}
                x2={W}
                y1={y}
                y2={y}
                stroke="var(--grid)"
                strokeDasharray="4 4"
                strokeWidth="1"
                fill="none"
              />
            ))}
          </g>

          {edges.map(([a, b], idx) => {
            const from = nodeById[a];
            const to = nodeById[b];
            if (!from || !to) return null;
            const x1 = xFor(from.x);
            const y1 = yFor(from.y);
            const x2 = xFor(to.x);
            const y2 = yFor(to.y);
            const isPending = grouped && !free && to.status === "pending";
            const dim =
              (effective !== null && effective !== a && effective !== b) ||
              (grouped &&
                legFilterStatus !== null &&
                legFilterStatus !== nodeKey(to) &&
                legFilterStatus !== nodeKey(from));

            let d: string;
            if (Math.abs(y1 - y2) < 1) {
              d = `M ${x1},${y1} L ${x2},${y2}`;
            } else {
              const mx = x1 + (x2 - x1) * 0.5;
              d = `M ${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2},${y2}`;
            }
            const edgeStroke = free
              ? resolveCategoryColor(to.categoryId, freeCategories, "var(--chart-2)")
              : isPending
                ? "var(--status-3)"
                : "var(--status-2)";

            return (
              <path
                key={idx}
                d={d}
                fill="none"
                stroke={edgeStroke}
                strokeWidth={isPending ? 1.2 : 1.5}
                strokeDasharray={isPending ? "4 5" : "0"}
                strokeLinecap="round"
                opacity={dim ? 0.12 : 1}
                style={{ transition: "opacity 0.15s" }}
              />
            );
          })}

          {nodes.map((n) => {
            const cx = xFor(n.x);
            const cy = yFor(n.y);
            const isJunction = n.type === "junction";
            const isDone = grouped && !free && n.status === "done";
            const isActive = grouped && !free && n.status === "active";
            const isPending = grouped && !free && n.status === "pending";
            const isHovered = effective === n.id;
            const catColor = resolveCategoryColor(
              n.categoryId,
              freeCategories,
              "var(--chart-1)",
            );
            const dim =
              (effective !== null && !isHovered) ||
              (grouped &&
                legFilterStatus !== null &&
                legFilterStatus !== nodeKey(n));

            return (
              <g
                key={n.id}
                ref={(el) => {
                  anchorRefs.current[n.id] = el;
                }}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) node.setHovered(n.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) node.setHovered(null);
                }}
                onClick={(e) =>
                  node.select(n.id, {
                    kind: "node",
                    id: n.id,
                    element: e.currentTarget,
                  })
                }
                data-module-type="fork-join"
                data-part-index={n.id}
                data-part-label={n.label}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.35 : 1}
              >
                {isJunction ? (
                  <>
                    <rect
                      x={cx - 7}
                      y={cy - 7}
                      width="14"
                      height="14"
                      transform={`rotate(45 ${cx} ${cy})`}
                      fill="var(--background)"
                      stroke={
                        free
                          ? catColor
                          : !grouped
                            ? "var(--status-2)"
                            : isDone
                              ? "var(--status-1)"
                              : isPending
                                ? grayscale
                                  ? "var(--status-neutral)"
                                  : "var(--amber)"
                                : "var(--status-neutral)"
                      }
                      strokeWidth="1.5"
                      strokeDasharray={isPending ? "3 3" : "0"}
                    />
                    <foreignObject
                      x={cx - 60}
                      y={cy + 14}
                      width={120}
                      height={32}
                      overflow="visible"
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          height: "100%",
                          color: "var(--subtle)",
                          fontSize: "10px",
                          lineHeight: 1.15,
                          textAlign: "center",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {stripInlineMarkdown(n.label)}
                      </div>
                    </foreignObject>
                  </>
                ) : (
                  <>
                    {isHovered && !isActive && (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={11}
                        fill="none"
                        stroke={free ? catColor : "var(--status-2)"}
                        strokeWidth="1.3"
                      />
                    )}
                    {free && <circle cx={cx} cy={cy} r={5} fill={catColor} />}
                    {!free && !grouped && (
                      <circle cx={cx} cy={cy} r={5} fill="var(--status-1)" />
                    )}
                    {grouped && (isDone || isActive) && (
                      <circle cx={cx} cy={cy} r={5} fill="var(--status-1)" />
                    )}
                    {grouped && isPending && (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill="var(--background)"
                        stroke="var(--status-neutral)"
                        strokeWidth="1.5"
                      />
                    )}
                    <foreignObject
                      x={cx - 60}
                      y={cy - 30}
                      width={120}
                      height={16}
                      overflow="visible"
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          height: "100%",
                          color: isHovered
                            ? "var(--foreground)"
                            : isPending
                              ? "var(--subtle)"
                              : "var(--muted-foreground)",
                          fontSize: "11px",
                          fontWeight: isHovered ? 600 : 500,
                          lineHeight: 1.15,
                          textAlign: "center",
                          overflowWrap: "anywhere",
                          transition: "color 0.15s",
                        }}
                      >
                        {stripInlineMarkdown(n.label)}
                      </div>
                    </foreignObject>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeNode !== null}
        anchor={activeNode ? anchorRefs.current[activeNode.id] ?? null : null}
        contentKey={activeNode ? String(activeNode.id) : undefined}
        title={stripInlineMarkdown(activeNode?.label) || undefined}
        rows={
          activeNode
            ? [
                ...(activeNode.type === "junction"
                  ? [{ color: "var(--status-2)", label: t("Junction"), value: "" }]
                  : []),
                ...(activeStatusLabel
                  ? [{ color: activeStatusColor, label: t("Status"), value: activeStatusLabel }]
                  : []),
              ]
            : []
        }
      />

      {showLegend && grouped && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={onLegendSelect}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
